// Tenant config — the white-label seam. The PLATFORM brand (MinPrilla Insights) is
// constant; the SUPPLIER you view as is switchable at runtime via the header picker
// (a demo stand-in for login — no auth). Switching a supplier scopes the manufacturer
// filter, own brands (raw/SKU detail allowed), the highlighted brand and the preset
// competitors. No component hardcodes a brand, a name or a logo — all derive from here.
//
// The `manufacturer` string is the ONLY supplier identity sent to the backend; the
// backend resolves it to source manufacturer-ids from its single source
// (semantic.MANUFACTURERS). The frontend deliberately holds NO manufacturer-id copy —
// that map lives in one place, server-side, so the two can never drift.

import { clearAllTrendFilters } from './trendFilters'
import { clearAssortmentCache } from './assortmentCache'

export interface TenantWordmark {
  lead: string // coloured in the accent
  rest: string // coloured in ink
  tagline?: string // muted suffix
}

// A supplier the user can "log in" as. `manufacturer` MUST match a backend key in
// semantic.MANUFACTURERS (case-insensitive) — otherwise the dos endpoints can't scope
// to it. Verified against the Vinnarmatchen facit (each report's top SKUs → mfr id).
export interface Supplier {
  key: string // stable id, persisted in localStorage
  label: string // shown in the picker
  manufacturer: string // supplier identity sent to the API (backend resolves the ids)
  ownBrands: string[] // own brands — own raw/SKU data is allowed
  brandHighlight: string // the own brand emphasised in market-share
  compareBrands: string[] // preset competitor brands — aggregate/share ONLY
  landingName: string // default ("Översikt") dashboard name
  hasDosLayer: boolean // a validated dos snapshot backs this supplier
}

export interface Tenant {
  id: string
  theme: string // selects the CSS overlay; '' or 'solvigo' = the Solvigo default
  productName: string // header/title/export footer
  logoLetter: string // glyph in the round brand disc
  wordmark: TenantWordmark // sidebar wordmark, drawn from parts
  manufacturer: string
  ownBrands: string[]
  brandHighlight: string
  compareBrands: string[]
  landingName: string
  dosLayerRef: string
}

// The constant platform identity (the product itself).
const PLATFORM = {
  id: 'minprilla',
  theme: 'minprilla',
  productName: 'MinPrilla Insights',
  logoLetter: 'P',
  wordmark: { lead: 'MIN', rest: 'PRILLA', tagline: 'smart dashboard' } as TenantWordmark,
  dosLayerRef: '2026M05',
}

// Suppliers present and reconciled in the data (mars–maj). Each `manufacturer` matches
// a backend key in semantic.MANUFACTURERS — STG, Swedish Match, BAT, Skruf, JTI, Luna.
// (Vont is intentionally absent: its source id isn't confirmed yet — report SKU 5500
// resolves to NUED, not Vont AB — so we don't fabricate a mapping.)
export const SUPPLIERS: Supplier[] = [
  {
    key: 'stg',
    label: 'Scandinavian Tobacco Group',
    manufacturer: 'STG', // backend aliases → "Scandinavian Tobacco Group" (ids 122, 297)
    ownBrands: ['XQS', 'ACE'],
    brandHighlight: 'XQS',
    compareBrands: ['Zyn', 'Velo'],
    landingName: 'STG – Overview',
    hasDosLayer: true,
  },
  {
    key: 'swedish-match',
    label: 'Swedish Match',
    manufacturer: 'Swedish Match',
    ownBrands: ['ZYN', 'General', 'Kaliber', 'Kapten', 'Göteborgs Rapé', 'Kronan', 'Catch'],
    brandHighlight: 'ZYN',
    compareBrands: ['Velo', 'LD'],
    landingName: 'Swedish Match – Overview',
    hasDosLayer: true,
  },
  {
    key: 'bat',
    label: 'British American Tobacco',
    manufacturer: 'British American Tobacco',
    ownBrands: ['VELO', 'Lundgrens snus', 'Lundgrens All White'],
    brandHighlight: 'VELO',
    compareBrands: ['Zyn', 'XQS'],
    landingName: 'BAT – Overview',
    hasDosLayer: true,
  },
  {
    key: 'skruf',
    label: 'Skruf Snus',
    manufacturer: 'Skruf Snus',
    ownBrands: ['Knox snus', 'Skruf', 'skruf SUPERWHITE', 'Zone', 'Smålands Brukssnus', 'X All White'],
    brandHighlight: 'Knox snus',
    compareBrands: ['Zyn', 'General'],
    landingName: 'Skruf – Overview',
    hasDosLayer: true,
  },
  {
    key: 'jti',
    label: 'Japan Tobacco International',
    manufacturer: 'Japan Tobacco International', // mfr id 129 (was mislabelled "Nordic Snus")
    ownBrands: ['LD'],
    brandHighlight: 'LD',
    compareBrands: ['General', 'Zyn'],
    landingName: 'JTI – Overview',
    hasDosLayer: true,
  },
  {
    key: 'luna',
    label: 'LUNA Corporate',
    manufacturer: 'LUNA Corporate', // mfr id 107
    ownBrands: ['77 Snus', 'Cafero'],
    brandHighlight: '77 Snus',
    compareBrands: ['Zyn', 'XQS'],
    landingName: 'Luna – Overview',
    hasDosLayer: true,
  },
  {
    // Total-market profile (MinPrilla's god-view): no scoping — every brand, the whole
    // market. Empty manufacturer → the cube/dos paths drop the filter.
    key: 'all',
    label: 'All suppliers (whole market)',
    manufacturer: '',
    ownBrands: [],
    brandHighlight: '',
    compareBrands: [],
    landingName: 'The market – Overview',
    hasDosLayer: true,
  },
]

const STORAGE_KEY = 'mp_supplier' // stores the manufacturer VALUE (scope string), not a key
const LABEL_KEY = 'mp_supplier_label' // label for an arbitrary (non-onboarded) manufacturer

// The active manufacturer scope string ('STG' / 'Swedish Match' / a numeric id / '' = market).
// Default = the whole-market overview ('' → "Alla märken"), NOT a single supplier: this is a
// 50+-supplier platform, so a fresh session must not open onto STG (or any one tillverkare).
// A supplier login is locked to its own manufacturer by App.tsx; the admin picks from there.
export function activeManufacturer(): string {
  if (typeof localStorage === 'undefined') return ''
  const v = localStorage.getItem(STORAGE_KEY)
  return v === null ? '' : v // '' (market) is the default and a valid stored value
}

// Resolve a manufacturer value to a Supplier: an onboarded one (rich brand config) or,
// for any other manufacturer the admin picks, a minimal scope-only profile so Olof can
// see its numbers (KPIs/segments/customers all scope by manufacturer; no brand-compare).
function resolveSupplier(value: string, label: string): Supplier {
  const known = SUPPLIERS.find((s) => s.manufacturer.toLowerCase() === value.trim().toLowerCase())
  if (known) return known
  const display = label || (/^[\d,\s]+$/.test(value) ? 'Your supplier' : value)
  return {
    key: value,
    label: display,
    manufacturer: value,
    ownBrands: [],
    brandHighlight: '',
    compareBrands: [],
    landingName: display,
    hasDosLayer: true,
  }
}

export const ACTIVE_SUPPLIER: Supplier = resolveSupplier(
  activeManufacturer(),
  (typeof localStorage !== 'undefined' && localStorage.getItem(LABEL_KEY)) || '',
)

// Switch supplier = persist + reload. A full reload re-resolves TENANT and re-applies
// the theme before React renders, so every chart/inline style picks up the new scope.
export function setSupplier(value: string, label?: string): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(STORAGE_KEY, value)
  localStorage.setItem(LABEL_KEY, label || '')
  location.reload()
}

// Refresh an existing supplier session too: old numeric labels must not survive
// simply because its authorization scope already matches the session.
export function syncSupplier(value: string, label?: string): void {
  if (typeof localStorage === 'undefined') return
  const display = label || resolveSupplier(value, '').label
  if (activeManufacturer().trim().toLowerCase() !== value.trim().toLowerCase()
      || localStorage.getItem(LABEL_KEY) !== display) setSupplier(value, display)
}

// The composed tenant the whole app reads — platform identity + the active supplier.
export const TENANT: Tenant = {
  ...PLATFORM,
  manufacturer: ACTIVE_SUPPLIER.manufacturer,
  ownBrands: ACTIVE_SUPPLIER.ownBrands,
  brandHighlight: ACTIVE_SUPPLIER.brandHighlight,
  compareBrands: ACTIVE_SUPPLIER.compareBrands,
  landingName: ACTIVE_SUPPLIER.landingName,
}

// Logout purge (AUDIT W1): wipe every client-side residue so a shared device retains no
// previous user's supplier scope or cached board state. Boards persist server-side keyed
// by the authenticated user (api _session_client) — the client_id + offline mirror are
// vestigial for scoping, so clearing them loses nothing. Call from the logout handler.
export function clearClientState(options?: { preserveDashboardBackups?: boolean }): void {
  if (typeof localStorage !== 'undefined') {
    const boardKeys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
      .filter((k): k is string => !!k && k.startsWith(`${TENANT.id}_dashboards_v2:`))
    if (!options?.preserveDashboardBackups) boardKeys.forEach((k) => localStorage.removeItem(k))
    for (const k of [STORAGE_KEY, LABEL_KEY, `${TENANT.id}_client_id`, `${TENANT.id}_dashboards`]) {
      localStorage.removeItem(k)
    }
  }
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem('mp_landed')
    sessionStorage.removeItem('mp_section')
  }
  // MP-OS-188 bug #5 — persisted Sales Trends filters are keyed by user + scope, so they are
  // already isolated between accounts; clearing them here means a shared device never even
  // retains the trail of a signed-out user's past filter choices.
  clearAllTrendFilters()
  clearAssortmentCache()
}
